/**
 * Format range tanggal dan jam dengan logika peringkasan
 * @param {string} startDate - Tanggal awal (format: YYYY-MM-DD)
 * @param {string} startTime - Jam awal (format: HH:mm:ss)
 * @param {string} endDate - Tanggal akhir (format: YYYY-MM-DD)
 * @param {string} endTime - Jam akhir (format: HH:mm:ss)
 * @returns {string} Range tanggal dan jam yang sudah diformat
 */
export const formatDateTimeRange = (startDate, startTime, endDate, endTime) => {
  // Validasi input
  if (!startDate || !startTime || !endDate || !endTime) {
    return '-';
  }
  
  // Parse tanggal
  const [startYear, startMonth, startDay] = startDate.split('-');
  const [endYear, endMonth, endDay] = endDate.split('-');
  
  // Parse jam
  const [startHour, startMinute] = startTime.split(':');
  const [endHour, endMinute] = endTime.split(':');
  
  // Nama bulan dalam bahasa Indonesia
  const monthNames = [
    'Jan', 'Feb', 'Mar', 'Apr', 'Mei', 'Jun',
    'Jul', 'Agu', 'Sep', 'Okt', 'Nov', 'Des'
  ];
  
  const startMonthName = monthNames[parseInt(startMonth) - 1];
  const endMonthName = monthNames[parseInt(endMonth) - 1];
  
  // Format jam
  const formattedStartTime = `${startHour}:${startMinute}`;
  const formattedEndTime = `${endHour}:${endMinute}`;
  
  // Logika peringkasan
  // Jika tahun, bulan, dan tanggal sama
  if (startYear === endYear && startMonth === endMonth && startDay === endDay) {
    return `${parseInt(startDay)} ${startMonthName} ${startYear}, ${formattedStartTime} - ${formattedEndTime}`;
  }
  
  // Jika tahun dan bulan sama, tapi tanggal berbeda
  if (startYear === endYear && startMonth === endMonth) {
    return `${parseInt(startDay)} - ${parseInt(endDay)} ${startMonthName} ${startYear}, ${formattedStartTime} - ${formattedEndTime}`;
  }
  
  // Jika tahun sama, tapi bulan berbeda
  if (startYear === endYear) {
    return `${parseInt(startDay)} ${startMonthName} - ${parseInt(endDay)} ${endMonthName} ${startYear}, ${formattedStartTime} - ${formattedEndTime}`;
  }
  
  // Jika semua berbeda
  return `${parseInt(startDay)} ${startMonthName} ${startYear} ${formattedStartTime} - ${parseInt(endDay)} ${endMonthName} ${endYear} ${formattedEndTime}`;
};

/**
 * Format range tanggal saja (tanpa jam)
 * @param {string} startDate - Tanggal awal (format: YYYY-MM-DD)
 * @param {string} endDate - Tanggal akhir (format: YYYY-MM-DD)
 * @returns {string} Range tanggal yang sudah diformat
 */
export const formatDateRange = (startDate, endDate) => {
  // Validasi input
  if (!startDate || !endDate) {
    return '-';
  }
  
  const [startYear, startMonth, startDay] = startDate.split('-');
  const [endYear, endMonth, endDay] = endDate.split('-');
  
  const monthNames = [
    'Jan', 'Feb', 'Mar', 'Apr', 'Mei', 'Jun',
    'Jul', 'Agu', 'Sep', 'Okt', 'Nov', 'Des'
  ];
  
  const startMonthName = monthNames[parseInt(startMonth) - 1];
  const endMonthName = monthNames[parseInt(endMonth) - 1];
  
  // Jika tahun, bulan, dan tanggal sama
  if (startYear === endYear && startMonth === endMonth && startDay === endDay) {
    return `${parseInt(startDay)} ${startMonthName} ${startYear}`;
  }
  
  // Jika tahun dan bulan sama, tapi tanggal berbeda
  if (startYear === endYear && startMonth === endMonth) {
    return `${parseInt(startDay)} - ${parseInt(endDay)} ${startMonthName} ${startYear}`;
  }
  
  // Jika tahun sama, tapi bulan berbeda
  if (startYear === endYear) {
    return `${parseInt(startDay)} ${startMonthName} - ${parseInt(endDay)} ${endMonthName} ${startYear}`;
  }
  
  // Jika semua berbeda
  return `${parseInt(startDay)} ${startMonthName} ${startYear} - ${parseInt(endDay)} ${endMonthName} ${endYear}`;
};

/**
 * Format range jam saja
 * @param {string} startTime - Jam awal (format: HH:mm:ss)
 * @param {string} endTime - Jam akhir (format: HH:mm:ss)
 * @returns {string} Range jam yang sudah diformat
 */
export const formatTimeRange = (startTime, endTime) => {
  // Validasi input
  if (!startTime || !endTime) {
    return '-';
  }
  
  const [startHour, startMinute] = startTime.split(':');
  const [endHour, endMinute] = endTime.split(':');
  
  return `${startHour}:${startMinute} - ${endHour}:${endMinute}`;
};

/**
 * Format range tanggal start dan end saja (sederhana)
 * @param {string} startDate - Tanggal awal (format: YYYY-MM-DD)
 * @param {string} endDate - Tanggal akhir (format: YYYY-MM-DD)
 * @returns {string} Range tanggal dalam format "DD MMM YYYY - DD MMM YYYY"
 */
export const formatSimpleDateRange = (startDate, endDate) => {
  // Validasi input
  if (!startDate || !endDate) {
    return '-';
  }
  
  const [startYear, startMonth, startDay] = startDate.split('-');
  const [endYear, endMonth, endDay] = endDate.split('-');
  
  const monthNames = [
    'Jan', 'Feb', 'Mar', 'Apr', 'Mei', 'Jun',
    'Jul', 'Agu', 'Sep', 'Okt', 'Nov', 'Des'
  ];
  
  const startMonthName = monthNames[parseInt(startMonth) - 1];
  const endMonthName = monthNames[parseInt(endMonth) - 1];
  
  // Jika tahun, bulan, dan tanggal sama
  if (startYear === endYear && startMonth === endMonth && startDay === endDay) {
    return `${parseInt(startDay)} ${startMonthName} ${startYear}`;
  }
  
  // Jika tahun dan bulan sama, tapi tanggal berbeda
  if (startYear === endYear && startMonth === endMonth) {
    return `${parseInt(startDay)} - ${parseInt(endDay)} ${startMonthName} ${startYear}`;
  }
  
  // Jika tahun sama, tapi bulan berbeda
  if (startYear === endYear) {
    return `${parseInt(startDay)} ${startMonthName} - ${parseInt(endDay)} ${endMonthName} ${startYear}`;
  }
  
  // Jika semua berbeda
  return `${parseInt(startDay)} ${startMonthName} ${startYear} - ${parseInt(endDay)} ${endMonthName} ${endYear}`;
};


